<template>

    <div id="logs">

        <div class="mb-4">
            <h1 class="text-2xl font-medium text-gray-900 capitalize">Logs</h1>
        </div>

        <div class="filter-bar" v-if="form">
            <ul class="grid-cols-3">
                <li>
                    <button @click="navigateSection(false)" :class="{ 'active': form.type === false }">
                        All
                    </button>
                </li>
                <li>
                    <button @click="navigateSection('successful')" :class="{ 'active': form.type === 'successful' }">
                        Successful
                    </button>
                </li>
                <li>
                    <button @click="navigateSection('failed')" :class="{ 'active': form.type === 'failed' }">
                        Failed
                    </button>
                </li>
            </ul>
            <div class="filters grid-cols-6">
                <div class="filter-control col-span-6 xl:col-span-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    <input id="filter_search" type="text" placeholder="Search by URI or Request ID" @keyup="submitFilterDelayed" v-model="form.filters.query">
                </div>
                <div class="filter-control col-span-3 xl:col-span-1 pl-14 ml-1 xl:pl-0 xl:ml-0">
                    <select id="filter_status" name="status" v-model="form.filters.status" @change="submitFilter">
                        <option :value="null">Status</option>
                        <option :value="status.value" v-for="status in http_statuses">{{ status.name }}</option>
                    </select>
                </div>
                <div class="filter-control col-span-2 xl:col-span-1">
                    <select id="filter_method" name="method" v-model="form.filters.method" @change="submitFilter">
                        <option :value="null">Method</option>
                        <option :value="method.value" v-for="method in http_methods">{{ method.name }}</option>
                    </select>
                </div>
                <div class="col-span-1">
                    <button class="btn-outline btn-clear-filters py-1 px-1" @click.prevent="resetFilters()">Clear Filters</button>
                </div>
            </div>
        </div>

        <div class="split-index-table" v-if="loaded">
            <div>
                <div class="index-table" v-if="form.index_data.data.length > 0" >
                    <div>
                        <div>
                            <table>
                                <thead>
                                    <tr class="flex">
                                        <th scope="col" class="w-1/2 xl:w-5/12 text-left tracking-wider">Request Logs</th>
                                        <th scope="col" class="w-1/2 xl:w-3/12 text-center tracking-wider">Status</th>
                                        <th scope="col" class="hidden xl:table-cell xl:w-4/12 text-left tracking-wider">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody class="flex flex-col justify-between overflow-y-scroll w-full max-h-96 xl:max-h-120">
                                    <tr class="flex px-2 colored-row cursor-pointer" :class="{ 'selected-row': form.selected.id === log.id }" v-for="(log, index) in form.index_data.data" @click.prevent="viewLog(index)">
                                        <td class="w-1/2 xl:w-5/12 justify-start">
                                            <span class="mr-2 status-box status-open" v-if="form.selected.id === log.id">
                                                {{ log.method }}
                                            </span>
                                            <span :class="`mr-2 ${statusBox(log.method)}`" v-else>
                                                {{ log.method }}
                                            </span>
                                            <span class="font-medium">{{ ellipsis(log.uri, 20) }}</span>
                                            <br>
                                            <span class="text-xs text-gray-500">{{ log.id }}</span>
                                        </td>
                                        <td class="w-1/2 xl:w-3/12 justify-center text-center">
                                            <span :class="`status-box ${statusBox(log.status)}`">
                                                {{ ellipsis(convertStatus(log.status), 16) }}
                                            </span><br>
                                            &nbsp;
                                        </td>
                                        <td class="hidden xl:table-cell xl:w-4/12 justify-end text-right">
                                            <span class="text-xs">{{ formatDate(log.created_at) }}</span><br>
                                            <span class="text-xs text-gray-500">Request made {{ log.created_human_readable }}</span>
                                        </td>
                                    </tr>
                                    <tr class="py-6 text-xs font-medium">
                                        <infinite-loading @infinite="infiniteHandler">
                                            <div slot="spinner">Loading...</div>
                                            <div slot="no-more">No more Logs</div>
                                        </infinite-loading>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="no-results panel" v-else>
                    <div class="p-10">
                        <div class="text-gray-200 my-6">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 21h7a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v11m0 5l4.879-4.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z" />
                            </svg>
                        </div>

                        <h2 class="text-gray-600 font-medium text-2xl text-center">No Logs Available</h2>

                        <p class="text-center mt-4 text-sm text-gray-500">
                            We could not find any logs.
                        </p>
                    </div>
                </div>
                <div class="index-table-preview max-h-96 xl:max-h-132" v-if="form.index_data.data.length > 0">
                    <div v-if="form.selected">
                        <h2 class="flex items-center font-medium text-2xl pb-2">
                            <span :class="`status-box mr-2 status-box status-open`">
                                {{ form.selected.method }}
                            </span>
                            {{ form.selected.uri }}
                        </h2>

                        <table class="text-sm mb-5 w-full">
                            <tbody>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Request ID</td>
                                <td class="w-4/6">{{ form.selected.id }}</td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Status</td>
                                <td class="w-4/6">
                                    <a :href="`https://httpstatuses.com/${form.selected.status}`" class="flex text-indigo-500 hover:text-indigo-400" target="_blank">
                                        <span :class="`status-box ${statusBox(form.selected.status)}`">
                                            {{ convertStatus(form.selected.status) }}
                                        </span>
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">User</td>
                                <td class="w-4/6">
                                <span v-if="form.selected.user_name">
                                    {{ form.selected.user_name }} &lt;<a :href="`mailto:${form.selected.user_email}`" target="_blank" class="text-brand-700">{{ form.selected.user_email }}</a>&gt;
                                </span>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">Date</td>
                                <td class="w-4/6">
                                    {{ form.selected.created_at }}
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">IP Address</td>
                                <td class="w-4/6">
                                    <a href="#" @click.prevent="form.ipLookup(form.selected.remote_address)">{{ form.selected.remote_address }}</a>
                                </td>
                            </tr>
                            <tr>
                                <td class="w-2/6 text-gray-400 py-1">User Agent</td>
                                <td class="w-4/6">
                                    {{ form.selected.user_agent }}
                                </td>
                            </tr>
                            </tbody>
                        </table>

                        <h3 class="font-medium mb-1 ml-4 text-base">Request Body</h3>

                        <div class="text-3xl">
                            <pre class="p-0 m-0"><code class="language-json" :id="`request-block-${form.selected.id}`">{{ form.selected.request ? form.selected.request : 'no request body' }}</code></pre>
                        </div>

                        <h3 class="font-medium mb-1 ml-4 text-base">Response Body</h3>

                        <div class="text-3xl">
                            <pre class="p-0 m-0"><code class="language-json" :id="`response-block-${form.selected.id}`">{{ form.selected.response ? form.selected.response : 'no request body' }}</code></pre>
                        </div>

                        <!-- IP Lookup Modal -->
                        <transition
                            enter-active-class="ease-out duration-300"
                            enter-from-class="opacity-0"
                            enter-to-class="opacity-100"
                            leave-active-class="ease-in duration-200"
                            leave-from-class="opacity-100"
                            leave-to-class="opacity-0"
                        >
                            <div class="fixed z-50 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-show="form.ip_lookup">
                                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                                    <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                                        <div>
                                            <h4 class="text-lg font-medium leading-6 text-gray-900">IP Lookup</h4>

                                            <table class="text-sm mb-5" v-if="form.ip_lookup">
                                                <tbody>
                                                <tr>
                                                    <td class="w-1/2 text-gray-400 py-1">IP</td>
                                                    <td>{{ form.ip_lookup.ip ? form.ip_lookup.ip : '-' }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="w-1/2 text-gray-400 py-1">City</td>
                                                    <td>{{ form.ip_lookup.city ? form.ip_lookup.city : '-' }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="w-1/2 text-gray-400 py-1">Region</td>
                                                    <td>{{ form.ip_lookup.region_name ? form.ip_lookup.region_name : '-' }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="w-1/2 text-gray-400 py-1">Country</td>
                                                    <td>{{ form.ip_lookup.location.country_flag_emoji }} {{ form.ip_lookup.country_name ? form.ip_lookup.country_name : '-' }}</td>
                                                </tr>
                                                <tr>
                                                    <td class="w-1/2 text-gray-400 py-1">Location</td>
                                                    <td>
                                                        <a :href="`https://www.openstreetmap.org/?mlat=${form.ip_lookup.latitude}&mlon=${form.ip_lookup.longitude}&zoom=12`" class="flex text-indigo-500 hover:text-indigo-400" target="_blank" v-if="form.ip_lookup.latitude">
                                                            {{ form.ip_lookup.latitude }} / {{ form.ip_lookup.longitude }}
                                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                            </svg>
                                                        </a>
                                                        <span v-else>-</span>
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>

                                        </div>
                                        <div class="mt-5 sm:mt-6 sm:grid sm:grid-cols-1 sm:gap-3 sm:grid-flow-row-dense">
                                            <button type="button" class="btn justify-center" @click.prevent="form.closeIpLookup()">
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </transition>

                    </div>
                    <div class="loading" v-else>
                        <Loading :message="false" :show_long_loading="false"></Loading>
                    </div>
                </div>
            </div>
        </div>
        <Loading message="Loading Logs" v-else></Loading>

    </div>

</template>

<script>
import Loading from "../Support/Loading";
import {Logs} from "../../app/Log/Logs";
import Prism from 'prismjs'
import 'prismjs/components/prism-json'
import ConfirmationModal from "../Support/ConfirmationModal";
import InfiniteLoading from "vue-infinite-loading";
import {FormattingMixin, HttpStatusMixin, PermissionMixin} from "../../support/mixins";

export default {
    name: "Logs",
    mixins: [PermissionMixin, FormattingMixin, HttpStatusMixin],
    data() {
        return {
            form: null,
            current_user: null,
            loaded: false,
            http_statuses: [
                {
                    value: 100,
                    name: '100 Continue',
                },
                {
                    value: 101,
                    name: '101 Switching Protocols',
                },
                {
                    value: 102,
                    name: '102 Processing',
                },
                {
                    value: 200,
                    name: '200 OK',
                },
                {
                    value: 201,
                    name: '201 Created',
                },
                {
                    value: 202,
                    name: '202 Accepted',
                },
                {
                    value: 203,
                    name: '203 Non-authoritative Information',
                },
                {
                    value: 204,
                    name: '204 No Content',
                },
                {
                    value: 205,
                    name: '205 Reset Content',
                },
                {
                    value: 206,
                    name: '206 Partial Content',
                },
                {
                    value: 207,
                    name: '207 Multi-Status',
                },
                {
                    value: 208,
                    name: '208 Already Reported',
                },
                {
                    value: 226,
                    name: '226 IM Used',
                },
                {
                    value: 300,
                    name: '300 Multiple Choices',
                },
                {
                    value: 301,
                    name: '301 Moved Permanently',
                },
                {
                    value: 302,
                    name: '302 Found',
                },
                {
                    value: 303,
                    name: '303 See Other',
                },
                {
                    value: 304,
                    name: '304 Not Modified',
                },
                {
                    value: 305,
                    name: '305 Use Proxy',
                },
                {
                    value: 307,
                    name: '307 Temporary Redirect',
                },
                {
                    value: 308,
                    name: '308 Permanent Redirect',
                },
                {
                    value: 400,
                    name: '400 Bad Request',
                },
                {
                    value: 401,
                    name: '401 Unauthorized',
                },
                {
                    value: 402,
                    name: '402 Payment Required',
                },
                {
                    value: 403,
                    name: '403 Forbidden',
                },
                {
                    value: 404,
                    name: '404 Not Found',
                },
                {
                    value: 405,
                    name: '405 Method Not Allowed',
                },
                {
                    value: 406,
                    name: '406 Not Acceptable',
                },
                {
                    value: 407,
                    name: '407 Proxy Authentication Required',
                },
                {
                    value: 408,
                    name: '408 Request Timeout',
                },
                {
                    value: 409,
                    name: '409 Conflict',
                },
                {
                    value: 410,
                    name: '410 Gone',
                },
                {
                    value: 411,
                    name: '411 Length Required',
                },
                {
                    value: 412,
                    name: '412 Precondition Failed',
                },
                {
                    value: 413,
                    name: '413 Payload Too Large',
                },
                {
                    value: 414,
                    name: '414 Request-URI Too Long',
                },
                {
                    value: 415,
                    name: '415 Unsupported Media Type',
                },
                {
                    value: 416,
                    name: '416 Requested Range Not Satisfiable',
                },
                {
                    value: 417,
                    name: '417 Expectation Failed',
                },
                {
                    value: 418,
                    name: '418 I\'m a teapot',
                },
                {
                    value: 421,
                    name: '421 Misdirected Request',
                },
                {
                    value: 422,
                    name: '422 Unprocessable Entity',
                },
                {
                    value: 423,
                    name: '423 Locked',
                },
                {
                    value: 424,
                    name: '424 Failed Dependency',
                },
                {
                    value: 426,
                    name: '426 Upgrade Required',
                },
                {
                    value: 428,
                    name: '428 Precondition Required',
                },
                {
                    value: 429,
                    name: '429 Too Many Requests',
                },
                {
                    value: 431,
                    name: '431 Request Header Fields Too Large',
                },
                {
                    value: 444,
                    name: '444 Connection Closed Without Response',
                },
                {
                    value: 451,
                    name: '451 Unavailable For Legal Reasons',
                },
                {
                    value: 499,
                    name: '499 Client Closed Request',
                },
                {
                    value: 500,
                    name: '500 Server Error',
                },
                {
                    value: 501,
                    name: '501 Not Implemented',
                },
                {
                    value: 502,
                    name: '502 Bad Gateway',
                },
                {
                    value: 503,
                    name: '503 Service Unavailable',
                },
                {
                    value: 504,
                    name: '504 Gateway Timeout',
                },
                {
                    value: 505,
                    name: '505 HTTP Version Not Supported',
                },
                {
                    value: 506,
                    name: '506 Variant Also Negotiates',
                },
                {
                    value: 507,
                    name: '507 Insufficient Storage',
                },
                {
                    value: 508,
                    name: '508 Loop Detected',
                },
                {
                    value: 510,
                    name: '510 Not Extended',
                },
                {
                    value: 511,
                    name: '511 Network Authentication Required',
                },
                {
                    value: 599,
                    name: '599 Network Connect Timeout Error',
                },
            ],
            http_methods: [
                { value: 'get', name: 'GET' },
                { value: 'post', name: 'POST' },
                { value: 'put', name: 'PUT' },
                { value: 'delete', name: 'DELETE' },
            ],
        }
    },
    components: {
        Loading,
        ConfirmationModal,
        InfiniteLoading
    },
    methods: {
        resetFilters() {
            let vm = this;
            vm.form.page = 1;

            vm.form.resetFilters().then(result => {
                vm.submitFilter();
            });
        },
        submitFilterDelayed() {
            let vm = this;
            vm.form.page = 1;

            clearTimeout(vm.timeout);

            vm.timeout = setTimeout(function() {
                vm.submitFilter();
            }, 600);
        },
        submitFilter() {
            this.form.getLogs().then(function () {
                Prism.highlightAll();
            });
        },
        navigateSection(section) {
            let vm = this;

            return new Promise(resolve => {
                vm.loaded = false;
                vm.form.type = section;
                vm.form.page = 1;

                vm.form.getLogs().then(result => {

                    vm.loaded = true;

                    resolve(result);
                }).then(result => {
                    vm.viewLog(0);
                });
            });
        },
        viewLog(index) {
            let vm = this;

            vm.form.selected = false;

            setTimeout(function () {
                vm.form.selectLog(index).then(response => {
                    Prism.highlightAll();
                });
            }, 200);

        },
        infiniteHandler($state) {
            let vm = this;

            vm.form.page += 1;

            if (vm.form.indexHasMoreResults()) {
                this.form.getLogs(true).then(response => {
                    $state.loaded();

                    Prism.highlightAll();
                });
            } else {
                $state.loaded();
                $state.complete();
            }
        },
    },
    mounted() {
        let vm = this;

        vm.type = vm.$route.params.type ?? false;

        new Promise(resolve => {
            vm.form = new Logs();

            vm.form.getLogs().then(response => {
                resolve(true);
            }).catch(error => vm.handleError(error));

        }).then(result => {
            vm.loaded = result;

            vm.viewLog(0);
        })
    }
}
</script>


